import React, { useEffect, useMemo, useState } from 'react';
import ProductCard from '../Components/ProductCards/ProductCard';
import BannerBlockItem from './BannerBlock/BannerBlockItem';
import { BannerBlockProps } from './BannerBlock/BannerBlock';
import { ProductNoSliderProps } from './ProductBlockNoSlider';
import { LinkModel } from '../Types/LinkModel';
import clsx from 'clsx';
import { getListingPricesJson } from '../Components/NavisionPrice/services/NavisionPriceService';
import { getListingstocksJson } from '../Components/NavisionStock/services/NavisionStockService';
import { useTypedSelector } from '../Hooks/useTypedSelector';
import { ProductStocks } from '../Components/NavisionStock/types/ProductStocks';
import useShowBothPrices from '../Hooks/useShowBothPrices';

interface ProductAndBannerProps {
  products: ProductNoSliderProps;
  banners: BannerBlockProps;
  showBannerToTheRight: boolean;
  productQuantity: number;
  title?: string;
  link?: LinkModel;
}

const ProductsAndBannerBlock = ({
  products: productsBlock,
  banners: bannersBlock,
  showBannerToTheRight,
  title,
  link,
}: ProductAndBannerProps) => {
  const bannerBlockItem = useMemo(
    () => (bannersBlock.banners?.length ? bannersBlock.banners[0] : null),
    [bannersBlock.banners]
  );

  const firstTwoProducts = useMemo(
    () => productsBlock.products?.slice(0, 2),
    [productsBlock]
  );
  const remainingProducts = useMemo(
    () => productsBlock.products?.slice(3),
    [productsBlock]
  );
  const numbersPerRowStyle = {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-5',
    6: 'lg:grid-cols-6',
  };

  const [navisionPrices, setNavisionPrices] = useState<
    ProductPriceResponseItem[]
  >([]);
  const showBothPrices = useShowBothPrices();
  const [navisionStocks, setNavisionStocks] = useState<ProductStocks[]>(
    []
  );
  const { currentType: currentPriceToggleType } = useTypedSelector(
    (state) => state.priceToggle
  );
  const { isInBuyableState } = useTypedSelector(
    (state) => state.buyableState
  );

  useEffect(() => {
    const fetchData = async () => {
      const productIds = productsBlock.products?.map((p) => p?.id) || [];

      if (currentPriceToggleType != null && productIds.length) {
        const resultStocks = await getListingstocksJson(productIds);
        if (resultStocks != null && resultStocks.success) {
          setNavisionStocks(resultStocks.items);
        } else {
          console.log(
            `Failed to fetch Navision stocks for productId: ${productIds.toString()}`
          );
        }
      }

      if (showBothPrices && productIds.length) {
        const prices = await getListingPricesJson(productIds);
        setNavisionPrices(prices);
      }
    };

    fetchData();
  }, []);

  if (!bannerBlockItem) return null;
  return (
    <>
      {title && (
        <div className="mb-4 flex items-center justify-between md:mb-6">
          <h2 className="uppercase">{title}</h2>
          {link?.href && (
            <a className="text-xl underline" href={link.href}>
              {link.text}
            </a>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2 [&_.banner-item-image]:h-full [&_.banner-item-image]:lg:aspect-auto">
        <div className={clsx(showBannerToTheRight && 'lg:hidden')}>
          <BannerBlockItem
            className="h-full w-full overflow-hidden rounded"
            {...bannerBlockItem}
            lazyLoadImage={true}
          />
        </div>
        {firstTwoProducts && (
          <div
            className={`flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-6 ${
              numbersPerRowStyle[firstTwoProducts.length]
            }`}
          >
            {firstTwoProducts.map((product, idx) => (
              <ProductCard
                key={product.id + 'mixed' + idx}
                product={product}
                lazyLoadImage={true}
                showBothPrices={showBothPrices}
                navisionPrice={navisionPrices?.find(
                  (p) => p?.productId === product.id
                )}
                navisionStock={navisionStocks?.find(
                  (p) => p?.productId === product?.id
                )}
                currentPriceToggleType={currentPriceToggleType}
                isInBuyableState={isInBuyableState}
              />
            ))}
          </div>
        )}
        {showBannerToTheRight && (
          <div className="hidden lg:flex">
            <BannerBlockItem
              className="h-full w-full overflow-hidden rounded"
              {...bannerBlockItem}
              lazyLoadImage={true}
            />
          </div>
        )}
      </div>
      {remainingProducts?.length ? (
        <div className="mt-4 grid gap-4 md:mt-6 md:grid-cols-2 md:gap-6 lg:grid-cols-4">
          {remainingProducts.map((product, idx) => (
            <ProductCard
              key={product.id + 'mixed' + idx}
              product={product}
              lazyLoadImage={true}
              showBothPrices={showBothPrices}
              navisionPrice={navisionPrices?.find(
                (p) => p?.productId === product.id
              )}
              navisionStock={navisionStocks?.find(
                (p) => p?.productId === product?.id
              )}
              currentPriceToggleType={currentPriceToggleType}
              isInBuyableState={isInBuyableState}
            />
          ))}
        </div>
      ) : null}
    </>
  );
};

export default ProductsAndBannerBlock;
